import { Box, Stack, Typography, useTheme } from "@mui/material";
import qaNoData from "assets/images/nodata/qaNoData.png";
import { DonutChart } from "components/charts";
import { useGetQaHomeScreenScore, useGlobalStore } from "controller";
import { useAtomValue } from "jotai";
import { useDashboardScope } from "../../../hooks/useDashboardScope";
import { activityPeriodAtom } from "../../../store";
import { ActivityCard } from "./ActivityCard";
import { ActivityEmptyState } from "./ActivityEmptyState";

/** Rounds to a whole percent, guarding the empty-period divide-by-zero. */
const toPercent = (value: number, total: number) =>
  total > 0 ? Math.round((value / total) * 100) : 0;

export const QualityAssuranceCard = () => {
  const theme = useTheme();
  const timezone = useGlobalStore((state) => state.timezone);
  const period = useAtomValue(activityPeriodAtom);
  const { mode, resourceIds } = useDashboardScope();

  // The home-screen QA endpoint scores one tech per call and has no "all"
  // keyword, so neither a whole-team nor a multi-member scope can be expressed.
  // Rather than show a number that covers some of the selection, the card says
  // so. Null holds the query.
  const isUnsupportedScope = mode === "all" || resourceIds.length > 1;
  const resourceId =
    !isUnsupportedScope && resourceIds.length === 1 ? resourceIds[0] : null;

  // Activity periods share the QA service's names 1:1, so no mapping is needed.
  // The timezone must match the Active tile's or the week/month boundaries drift.
  const { data, isFetching } = useGetQaHomeScreenScore({
    resourceId,
    timePeriod: period,
    timeZone: timezone,
  });

  const passed = data?.passed ?? 0;
  const failed = data?.failed ?? 0;
  const total = data?.total ?? 0;

  const serviceLabel = total === 1 ? "Ticket" : "Tickets";

  const chartData = [
    { name: "Passed", value: passed, color: theme.palette.success.main },
    { name: "Failed", value: failed, color: theme.palette.error.main },
  ];

  // A 0-total donut with two 0% stats reads as broken rather than idle. Checked
  // after the unsupported-scope branch, whose message explains something the
  // empty state can't. An API error lands here too — `data` stays undefined.
  const isEmpty = !isFetching && total === 0;

  return (
    <ActivityCard title="Quality assurance">
      {isUnsupportedScope ? (
        <Stack
          sx={{
            flex: 1,
            minHeight: "160px",
            alignItems: "center",
            justifyContent: "center",
            px: "16px",
          }}
        >
          <Typography
            variant="body-small"
            sx={{ color: theme.palette.lightBg.low, textAlign: "center" }}
          >
            Select a single team member to view their QA score.
          </Typography>
        </Stack>
      ) : isEmpty ? (
        <ActivityEmptyState
          image={qaNoData}
          title="No QA reviews yet"
          description="No tickets have been reviewed yet."
          imageWidth="55px"
        />
      ) : (
        <Stack
          direction="row"
          sx={{
            flex: 1,
            minHeight: "160px",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
          }}
        >
          <Box sx={{ width: "138px", height: "138px", flexShrink: 0 }}>
            <DonutChart
              data={total > 0 ? chartData : []}
              labelKey="name"
              valueKey="value"
              width="100%"
              height="100%"
              innerRadius="62%"
              outerRadius="90%"
              centerContent={String(total)}
              centerSubContent={serviceLabel}
              isLoading={isFetching}
              showLegends={false}
            />
          </Box>

          <Stack sx={{ minWidth: 0, gap: "16px" }}>
            <QaStat
              label="Passed"
              color={theme.palette.primitive.system.green[700] as string}
              percent={toPercent(passed, total)}
            />
            <QaStat
              label="Failed"
              color={theme.palette.primitive.system.red[800] as string}
              percent={toPercent(failed, total)}
            />
          </Stack>
        </Stack>
      )}
    </ActivityCard>
  );
};

const QaStat = ({
  label,
  color,
  percent,
}: {
  label: string;
  color: string;
  percent: number;
}) => {
  const theme = useTheme();

  return (
    <Stack sx={{ gap: "4px" }}>
      <Stack direction="row" sx={{ alignItems: "center", gap: "4px" }}>
        <Box
          sx={{
            flexShrink: 0,
            width: "8px",
            height: "8px",
            borderRadius: "2px",
            backgroundColor: color,
          }}
        />
        <Typography
          variant="caption-3"
          sx={{ color: theme.palette.lightBg.low }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography variant="h5" sx={{ color: theme.palette.lightBg.high }}>
        {percent}%
      </Typography>
    </Stack>
  );
};
